/**
 * FILTER PANEL
 * Reusable filter sidebar/panel for cost data
 * Saves filter state to URL query params
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X } from 'lucide-react';

export default function FilterPanel({ onApply, onReset, availableFilters = {} }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(true);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    tenant_ids: [],
    providers: [],
    event_types: [],
    campaign_id: ''
  });

  // Load filters from URL on mount
  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    setFilters({
      date_from: params.date_from || '',
      date_to: params.date_to || '',
      tenant_ids: params.tenant_ids ? params.tenant_ids.split(',') : [],
      providers: params.providers ? params.providers.split(',') : [],
      event_types: params.event_types ? params.event_types.split(',') : [],
      campaign_id: params.campaign_id || ''
    });
  }, [searchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleMultiSelectToggle = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  const handleApply = () => {
    // Update URL params
    const newParams = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        newParams[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
    setSearchParams(newParams);

    // Call parent callback
    if (onApply) onApply(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      date_from: '',
      date_to: '',
      tenant_ids: [],
      providers: [],
      event_types: [],
      campaign_id: ''
    };
    setFilters(resetFilters);
    setSearchParams({});
    if (onReset) onReset();
  };

  const handlePreset = (preset) => {
    const today = new Date();
    const localStr = (d) => d.toLocaleDateString('en-CA');
    let date_from;

    switch (preset) {
      case 'today':
        date_from = localStr(today);
        break;
      case 'yesterday': {
        const d = new Date(today); d.setDate(d.getDate() - 1);
        date_from = localStr(d);
        break;
      }
      case 'last7days': {
        const d = new Date(today); d.setDate(d.getDate() - 7);
        date_from = localStr(d);
        break;
      }
      case 'last30days': {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        date_from = localStr(d);
        break;
      }
      case 'thisMonth':
        date_from = localStr(new Date(today.getFullYear(), today.getMonth(), 1));
        break;
      case 'lastMonth': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilters(prev => ({
          ...prev,
          date_from: localStr(start),
          date_to: localStr(end)
        }));
        return;
      }
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      date_from,
      date_to: localStr(today)
    }));
  };

  return (
    <div className={`bg-white rounded-lg shadow transition-all ${isOpen ? 'w-80' : 'w-16'}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {isOpen && (
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </h3>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          {isOpen ? <X className="w-5 h-5" /> : <Filter className="w-5 h-5" />}
        </button>
      </div>

      {isOpen && (
        <div className="p-4 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Date Range Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Select</label>
            <div className="grid grid-cols-2 gap-2">
              {['today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth'].map(preset => (
                <button
                  key={preset}
                  onClick={() => handlePreset(preset)}
                  className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  {preset.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <div className="space-y-2">
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Provider Filter */}
          {availableFilters.providers && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
              <div className="space-y-2">
                {['twilio', 'anthropic', 'openai', 'supabase'].map(provider => (
                  <label key={provider} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.providers.includes(provider)}
                      onChange={() => handleMultiSelectToggle('providers', provider)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{provider}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Event Type Filter */}
          {availableFilters.event_types && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
              <div className="space-y-2">
                {['sms_outbound', 'sms_inbound', 'ai_input_tokens', 'ai_output_tokens'].map(type => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.event_types.includes(type)}
                      onChange={() => handleMultiSelectToggle('event_types', type)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{type.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Tenant/Customer ID Filter */}
          {availableFilters.tenants && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer ID (Tenant ID)
              </label>
              <input
                type="text"
                value={filters.tenant_ids.join(',')}
                onChange={(e) => handleFilterChange('tenant_ids', e.target.value ? e.target.value.split(',').map(id => id.trim()) : [])}
                placeholder="Enter tenant_id(s), comma separated"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste tenant_id from users_profile table
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={handleApply}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Apply
            </button>
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
